import { useMemo } from 'react'
import { Badge } from 'components/common'
import { formatDate } from 'utils/helpers/date'
import { AggregatorFtpConfig } from 'types/aggregator'

// Same fallbacks the backend uses when the fields are left blank
const DEFAULT_MAX_RUNTIME_MINUTES = 120
const DEFAULT_MAX_HOURS_WITHOUT_RUN = 5
// Heartbeat refreshes every 30s - 3 missed means the run may have died
const STALE_HEARTBEAT_MS = 90_000

const toTime = (date?: string) => (date ? new Date(date).getTime() : 0)

interface TimeTileProps {
  label: string
  date?: string
  emptyText?: string
}

const TimeTile = ({ label, date, emptyText = '-' }: TimeTileProps) => (
  <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
      {label}
    </p>
    <p className="mt-0.5 text-sm font-semibold text-gray-800">
      {date ? formatDate(date, { includeTime: true }) : emptyText}
    </p>
  </div>
)

interface FtpRunStatusProps {
  ftp?: Partial<AggregatorFtpConfig>
  isAggregatorActive?: boolean
}

/**
 * View-only snapshot of this aggregator's scheduled FTP run, as of page load,
 * with warnings matching the backend's overrun and not-run alerts.
 */
const FtpRunStatus = ({ ftp, isAggregatorActive }: FtpRunStatusProps) => {
  const status = useMemo(() => {
    const now = Date.now()
    const isRunning = Boolean(ftp?.isRunning)
    const isEnabled = Boolean(ftp?.isEnable) && isAggregatorActive !== false

    const maxRuntimeMs =
      (Number(ftp?.maxRuntimeMinutes) || DEFAULT_MAX_RUNTIME_MINUTES) * 60_000
    const maxHoursMs =
      (Number(ftp?.maxHoursWithoutRun) || DEFAULT_MAX_HOURS_WITHOUT_RUN) *
      3_600_000

    const runStartedAt = toTime(ftp?.runStartedAt)
    const heartbeatAt = toTime(ftp?.lastHeartbeatAt)
    // Same baseline as the backend not-run monitor
    const sinceLastRun = Math.max(
      toTime(ftp?.lastRunCompletedAt),
      toTime(ftp?.cronScheduledAt),
    )

    return {
      isRunning,
      isEnabled,
      isOverRuntime:
        isRunning && runStartedAt > 0 && now - runStartedAt > maxRuntimeMs,
      isHeartbeatStale:
        isRunning && heartbeatAt > 0 && now - heartbeatAt > STALE_HEARTBEAT_MS,
      isOverdue:
        isEnabled &&
        !isRunning &&
        sinceLastRun > 0 &&
        now - sinceLastRun > maxHoursMs,
      checkedAt: formatDate(new Date(now), { includeTime: true }),
    }
  }, [ftp, isAggregatorActive])

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        {!status.isEnabled ? (
          <Badge color="gray">Disabled</Badge>
        ) : status.isRunning ? (
          <Badge color="green">Running</Badge>
        ) : (
          <Badge color="blue">Idle</Badge>
        )}
        {status.isOverRuntime && <Badge color="orange">Over max runtime</Badge>}
        {status.isHeartbeatStale && (
          <Badge color="yellow">Heartbeat stale</Badge>
        )}
        {status.isOverdue && <Badge color="red">Run overdue</Badge>}

        <span className="ml-auto text-[11px] text-gray-400">
          As of {status.checkedAt}
        </span>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 mid-md:grid-cols-4">
        <TimeTile
          label="Current run started"
          date={status.isRunning ? ftp?.runStartedAt : undefined}
          emptyText="Not running"
        />
        <TimeTile
          label="Last heartbeat"
          date={status.isRunning ? ftp?.lastHeartbeatAt : undefined}
          emptyText="Not running"
        />
        <TimeTile
          label="Last completed run"
          date={ftp?.lastRunCompletedAt}
          emptyText="Never"
        />
        <TimeTile label="Cron registered" date={ftp?.cronScheduledAt} />
      </div>
    </div>
  )
}

export default FtpRunStatus
